import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon as HeartIconOutline } from '@heroicons/react/24/outline'; // Import outlined heart icon
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'; // Import solid heart icon
import {
  saveMovieToFavorites,
  removeMovieFromFavorites,
  getUserFavorites,
} from '../utils/auth.js'; // Import functions to save and remove liked movies
import { auth } from '../utils/firebase.js'; // Firebase auth
import BannerAd from '../components/BannerAd.js';
// import LargerAd from "../components/LargerAd.js";

const Home = ({ searchTerm }) => {
  const API_KEY = process.env.REACT_APP_TMDB_API_KEY; // Access the API key from environment variables
  const [movies, setMovies] = useState([]);
  const timerRef = useRef(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const [likedMovies, setLikedMovies] = useState([]); // Ensure likedMovies is initialized as an array

  const user = auth.currentUser;

  useEffect(() => {
    // Update the document title
    document.title = 'Home - AfterCreds';

    // Handle meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Home page of AfterCreds.com.';

    // Handle Open Graph title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = 'Home - AfterCreds';

    // Handle Open Graph description
    let ogDescription = document.querySelector(
      'meta[property="og:description"]',
    );
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content =
      'Explore the latest movies and features of AfterCreds.';

    // Handle Open Graph URL
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = 'https://www.aftercreds.com';

    // Inject structured data for SEO purposes
    if (movies.length > 0) {
      // Inject structured data for SEO purposes
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Recent Movies',
        itemListElement: movies.map((movie, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Movie',
            name: movie.title,
            url: `https://www.aftercreds.com/movie/${movie.title
              .replace(/\s+/g, '-')
              .replace(/[^\w-]/g, '')}/${movie.id}`,
            image: `https://image.tmdb.org/t/p/w500/${movie.poster_path}`,
            datePublished: movie.release_date,
            description: movie.overview,
          },
        })),
      };

      let existingScript = document.querySelector(
        'script[type="application/ld+json"]',
      );
      if (existingScript) {
        existingScript.remove();
      }

      const scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.textContent = JSON.stringify(structuredData);
      document.head.appendChild(scriptTag);

      return () => {
        // Clean up metadata and structured data on unmount or movie change
        document.title = 'AfterCreds - Movie Database';
        metaDescription.content =
          'A movie website where users can browse their favorite movies and see if they have post-credit scenes.';
        ogTitle.content = 'AfterCreds - Movie Database';
        ogDescription.content =
          'Browse and view the latest movies for information on post-credit scenes.';
        ogUrl.content = 'https://www.aftercreds.com';

        document.head.removeChild(scriptTag);
      };
    }
  }, [movies]); // Runs whenever the movies array changes

  // Fetch movies with a delay when searching
  useEffect(() => {
    const fetchMoviesWithCertifications = async (moviesArray) => {
      const moviesWithCertifications = await Promise.all(
        moviesArray.map(async (movie) => {
          try {
            const response = await fetch(
              `https://api.themoviedb.org/3/movie/${movie.id}/release_dates?api_key=${API_KEY}`,
            );
            const data = await response.json();

            // Extract the U.S. release certification
            const usRelease = data.results?.find(
              (release) => release.iso_3166_1 === 'US',
            );
            const certification =
              usRelease?.release_dates.find((release) => release.type === 3)
                ?.certification || 'NR';

            return { ...movie, certification }; // Store certification in movie object
          } catch (error) {
            console.error(
              `Error fetching certification for ${movie.id}:`,
              error,
            );
            return { ...movie, certification: 'NR' }; // Default to "NR" if an error occurs
          }
        }),
      );
      return moviesWithCertifications;
    };

    if (searchTerm) {
      clearTimeout(timerRef.current);
      setLoading(true);
      timerRef.current = setTimeout(async () => {
        try {
          const response = await fetch(
            `https://api.themoviedb.org/3/search/movie?api_key=${API_KEY}&query=${searchTerm}&language=en-US&region=US&sort_by=popularity.desc&include_adult=false`,
          );
          const data = await response.json();

          const moviesWithCertifications = await fetchMoviesWithCertifications(
            data.results,
          );
          setMovies(moviesWithCertifications);
        } catch (error) {
          console.error('Error fetching search results:', error);
        }
        setLoading(false);
      }, 1000);
    } else {
      const fetchRecentMovies = async () => {
        setLoading(true);
        try {
          let movies = [];
          let movieIds = new Set();
          let page = 1;

          const now = new Date();
          const sixtyDaysAgo = new Date();
          sixtyDaysAgo.setDate(now.getDate() - 60);
          const thirtyDaysAhead = new Date();
          thirtyDaysAhead.setDate(now.getDate() + 30);

          // 1. Fetch from /movie/popular as usual
          while (movies.length < 30 && page <= 5) {
            const response = await fetch(
              `https://api.themoviedb.org/3/movie/popular?api_key=${API_KEY}&language=en-US&region=US&page=${page}`,
            );
            const data = await response.json();
            if (!data.results) break;

            const moviePromises = data.results.map(async (movie) => {
              if (movie.poster_path && !movieIds.has(movie.id)) {
                try {
                  const releaseRes = await fetch(
                    `https://api.themoviedb.org/3/movie/${movie.id}/release_dates?api_key=${API_KEY}`,
                  );
                  const releaseData = await releaseRes.json();

                  const usRelease = releaseData.results?.find(
                    (r) => r.iso_3166_1 === 'US',
                  );

                  const hasRelevantType = usRelease?.release_dates.some(
                    (r) => r.type === 3 || r.type === 4 || r.type === 6,
                  );

                  const releaseDate = new Date(movie.release_date);
                  const isRecentOrUpcoming =
                    releaseDate >= sixtyDaysAgo &&
                    releaseDate <= thirtyDaysAhead;

                  const certification =
                    usRelease?.release_dates.find((r) => r.type === 3)
                      ?.certification ||
                    usRelease?.release_dates.find((r) => r.type === 4)
                      ?.certification ||
                    usRelease?.release_dates.find((r) => r.type === 6)
                      ?.certification ||
                    'NR';

                  if (
                    (hasRelevantType || isRecentOrUpcoming) &&
                    movies.length < 30
                  ) {
                    let platform = 'theater'; // default

                    const providerResponse = await fetch(
                      `https://api.themoviedb.org/3/movie/${movie.id}/watch/providers?api_key=${API_KEY}`,
                    );
                    const providerData = await providerResponse.json();
                    const usProviders = providerData.results?.US?.flatrate;

                    if (usProviders && usProviders.length > 0) {
                      const provider =
                        usProviders[0].provider_name.toLowerCase();
                      if (provider.includes('netflix')) platform = 'netflix';
                      else if (provider.includes('hulu')) platform = 'hulu';
                      else if (provider.includes('apple')) platform = 'appletv';
                      else if (provider.includes('prime')) platform = 'prime';
                      else platform = 'streaming';
                    }

                    movies.push({ ...movie, certification, platform });
                    movieIds.add(movie.id);
                  }
                } catch (err) {
                  console.warn(
                    'Skipping movie due to error:',
                    movie.title,
                    err,
                  );
                }
              }
            });

            await Promise.all(moviePromises);
            page++;
          }

          // 2. Supplement with Netflix-originated content (via /discover/movie)
          const netflixRes = await fetch(
            `https://api.themoviedb.org/3/discover/movie?api_key=${API_KEY}&with_watch_providers=8&watch_region=US&sort_by=popularity.desc&language=en-US`,
          );
          const netflixData = await netflixRes.json();

          const netflixMovies = netflixData.results.filter((movie) => {
            const releaseDate = new Date(movie.release_date);
            return (
              movie.poster_path &&
              !movieIds.has(movie.id) &&
              releaseDate >= sixtyDaysAgo &&
              releaseDate <= thirtyDaysAhead
            );
          });

          // Add Netflix movies into the list
          netflixMovies.forEach((movie) => {
            movies.push({ ...movie, certification: 'NR', platform: 'netflix' });
            movieIds.add(movie.id);
          });

          // 3. Sort final list by release date, newest first
          movies.sort(
            (a, b) => new Date(b.release_date) - new Date(a.release_date),
          );

          // 4. Limit to 30
          setMovies(movies.slice(0, 30));
        } catch (error) {
          console.error('Error fetching movies:', error);
        }
        setLoading(false);
      };

      fetchRecentMovies();
    }
  }, [searchTerm, API_KEY]);

  // Fetch liked movies for the user when the component mounts
  useEffect(() => {
    const fetchLikedMovies = async () => {
      if (user) {
        const likedMoviesData = await getUserFavorites(user.email); // Use email here
        setLikedMovies(Array.isArray(likedMoviesData) ? likedMoviesData : []); // Ensure likedMovies is an array
      }
    };
    fetchLikedMovies();
  }, [user]);

  // Handle like or unlike a movie
  const handleLike = async (movie) => {
    if (!user) {
      alert('You need to be logged in to like a movie.');
      return;
    }

    const isMovieLiked = likedMovies.includes(movie.id);

    if (isMovieLiked) {
      await removeMovieFromFavorites(user.email, movie.id); // Use email here
      setLikedMovies(likedMovies.filter((id) => id !== movie.id));
    } else {
      await saveMovieToFavorites(user.email, movie.id); // Use email here
      setLikedMovies([...likedMovies, movie.id]);
    }
  };

  // Utility function to format date as mm-dd-yyyy
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  return (
    <div className="font-custom min-h-screen bg-gray-200 dark:bg-gray-900">
      {/* Header Section */}
      {!searchTerm && (
        <div className="container mx-auto pt-32 text-center px-4 sm:px-0">
          <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">
            Welcome to <span className="text-green-400">aftercreds</span>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Your go-to platform for discovering movies and tracking
            after-credits scenes. Browse the latest releases and keep track of
            your favorite movies.
          </p>
        </div>
      )}

      {/* Movie Cards */}
      <div
        className={`container mx-auto py-16 px-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 ${
          !searchTerm ? '' : 'pt-32'
        }`}
      >
        {loading ? (
          // Display placeholder cards when loading
          Array(10)
            .fill(0)
            .map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-300 dark:bg-gray-700 h-64 rounded-lg"></div>
                <div className="mt-2 h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="mt-1 h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))
        ) : movies.length === 0 ? (
          // Display a message if no movies are found
          <p className="pt-16 text-center text-gray-700 dark:text-gray-300 w-full col-span-5">
            No movies found.
          </p>
        ) : (
          // Display actual movie cards when data is loaded
          movies.map((movie, index) =>
            movie ? (
              <React.Fragment key={movie.id}>
                {/* Movie card (1 grid item) */}
                <div className="relative">
                  <Link
                    to={`/movie/${movie.title
                      .replace(/\s+/g, '-')
                      .replace(/[^\w-]/g, '')}/${movie.id}`}
                  >
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg shadow-md overflow-hidden transform hover:scale-105 hover:border-green-400 hover:border-2 transition duration-300 ease-in-out">
                      {movie.poster_path ? (
                        <img
                          src={`https://image.tmdb.org/t/p/w500/${movie.poster_path}`}
                          alt={movie.title}
                          className="w-full aspect-[2/3] object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-[2/3] flex items-center justify-center bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-center text-sm">
                          No Poster Available
                        </div>
                      )}

                      <div className="p-4">
                        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200">
                          {movie.title.length > 23
                            ? `${movie.title.substring(0, 23)}...`
                            : movie.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {movie.release_date
                            ? formatDate(movie.release_date)
                            : 'N/A'}
                          {movie.certification &&
                            movie.certification !== 'NR' && (
                              <span> &bull; {movie.certification}</span>
                            )}
                        </p>
                      </div>
                    </div>
                  </Link>

                  {/* Like Button */}
                  <button
                    className="absolute top-2 right-2 focus:outline-none"
                    onClick={() => handleLike(movie)}
                  >
                    {Array.isArray(likedMovies) &&
                    likedMovies.includes(movie.id) ? (
                      <HeartIconSolid className="w-10 h-10 text-red-500" />
                    ) : (
                      <HeartIconOutline className="w-10 h-10 text-gray-300 dark:text-gray-500" />
                    )}
                  </button>
                </div>

                {/* LargerAd inserted cleanly into grid layout */}
                {(index + 1) % 10 === 0 && index + 1 < movies.length && (
                  <div className="col-span-full w-full flex justify-center">
                    {' '}
                    {/* For testing: bg-white dark:bg-gray-800*/}
                    <BannerAd />
                  </div>
                )}
              </React.Fragment>
            ) : null,
          )
        )}
      </div>

      {/* Final BannerAd manually placed at footer */}
      <div className="container mx-auto px-4 pb-16">
        <div className="w-full flex justify-center">
          {' '}
          {/* For testing: bg-white dark:bg-gray-800*/}
          <BannerAd />
        </div>
      </div>
    </div>
  );
};

export default Home;
